import { useCallback, useEffect, useState } from 'react';

const THEME_KEY = 'atmwater-admin-theme';
const VALID_THEMES = new Set(['light', 'dark']);

export const getInitialAdminTheme = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const stored = window.localStorage.getItem(THEME_KEY);
  if (VALID_THEMES.has(stored)) {
    return stored;
  }

  return 'light';
};

export const applyAdminTheme = (theme) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.dataset.adminTheme = theme;
};

export const persistAdminTheme = (theme) => {
  if (!VALID_THEMES.has(theme)) {
    return;
  }

  if (typeof window !== 'undefined') {
    window.localStorage.setItem(THEME_KEY, theme);
    window.dispatchEvent(new CustomEvent('admin-theme-change', { detail: theme }));
  }

  applyAdminTheme(theme);
};

export default function useAdminTheme() {
  const [theme, setTheme] = useState(getInitialAdminTheme);

  useEffect(() => {
    applyAdminTheme(theme);
  }, [theme]);

  useEffect(() => {
    const handleThemeChange = (event) => {
      if (VALID_THEMES.has(event.detail)) {
        setTheme(event.detail);
      }
    };

    const handleStorage = (event) => {
      if (event.key === THEME_KEY && VALID_THEMES.has(event.newValue)) {
        setTheme(event.newValue);
      }
    };

    window.addEventListener('admin-theme-change', handleThemeChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('admin-theme-change', handleThemeChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setAdminTheme = useCallback((nextTheme) => {
    const resolvedTheme = typeof nextTheme === 'function' ? nextTheme(theme) : nextTheme;
    if (VALID_THEMES.has(resolvedTheme)) {
      persistAdminTheme(resolvedTheme);
      setTheme(resolvedTheme);
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setAdminTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'));
  }, [setAdminTheme]);

  return {
    theme,
    isDark: theme === 'dark',
    setAdminTheme,
    toggleTheme
  };
}
